
import { AlertCircle } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { LeakCard, LeakItem } from './LeakCard';
import { motion, AnimatePresence } from 'framer-motion';

interface LeaksGridProps {
  items: LeakItem[];
  formatDate: (dateString: string) => string;
  formatNumber: (num: number) => string;
  isLoading: boolean;
  error: string | null;
}

export function LeaksGrid({ items, formatDate, formatNumber, isLoading, error }: LeaksGridProps) {
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          <p className="mt-4 text-muted-foreground">{t('leaks.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-destructive">{t('leaks.error')}: {error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-10">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{t('leaks.noResults')}</p>
      </div>
    );
  }

  return (
    <motion.div
      layout
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      <AnimatePresence mode="popLayout">
        {items.map((item, index) => (
          <LeakCard
            key={item.domain + index}
            item={item}
            index={index}
            formatDate={formatDate}
            formatNumber={formatNumber}
          />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}
